import {useEffect, useState} from 'react';
import {Flow, FlowFetchResponse} from '../_services/Flow';
import {SearchResultsData} from '../types';

/**
 * Interface defining the return value of useSearch custom hook.
 * @interface UseSearchReturn
 *
 * @property {boolean} isSearching
 * A boolean indicating if the search operation is in progress.
 *
 * @property {SearchResultsData} searchResults
 * SearchResultsData is an object defining the structure of a search result.
 *
 * @property {string} searchErrorMessage
 * A string that stores the error message if an error occurs during the search operation.
 *
 * @property {object} searchFilters
 * An object representing the current search filters.
 *
 * @property {(searchFiltersState: object | ((prevState: object) => object)) => void} setSearchFilters
 * A function that lets you update the state of search filters. Accepts a newFiltersState parameters which could be an object or a function that takes in the previous state and returns a new state.
 *
 * @property {() => void} clearSearchResultEntries
 * A function that clears the entries of the search result.
 *
 * @property {(page: number, page_size: number) => void} changePage
 * A function that facilitates pagination. Accepts a page number and page size to switch to the specified page.
 */
interface UseSearchReturn {
    isSearching: boolean;
    searchResults: SearchResultsData;
    searchErrorMessage: string;
    searchFilters: object;
    setSearchFilters: (searchFiltersState: object | ((prevState: object) => object)) => void;
    clearSearchResultEntries: () => void;
    changePage: (page: number, page_size: number) => void;
}

/**
 * Custom hook for handling search functionality
 *
 * @param {boolean} mine - Flag to indicate if the search is limited to the current user
 * @param {Object} initialSearchFilters - Initial search filters object
 *
 * @returns {UseSearchReturn} Object containing search-related state and functions
 */
export const useSearch = (
    mine = false,
    initialSearchFilters = {}
): UseSearchReturn => {
    const [searchFilters, setSearchFilters] = useState({...initialSearchFilters});
    const [searchResults, setSearchResults] = useState<SearchResultsData>({} as SearchResultsData);
    const [isSearching, setIsSearching]: any = useState(false);
    const [searchErrorMessage, setSearchErrorMessage]: any = useState('');

    useEffect(() => {
        setIsSearching(true);
        setSearchErrorMessage('');

        Flow.getInstance().search(mine, searchFilters)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json) {
                    setSearchResults(response.json);
                } else {
                    throw Error(response?.json?.message);
                }
            })
            .catch((error: any) => {
                setSearchErrorMessage(error?.message || 'Exception performing search');
            })
            .finally(() => {
                setIsSearching(false);
            });

    }, [searchFilters]);

    const clearSearchResultEntries = () => {
        setSearchResults({...searchResults, entries: []});
    }

    const changePage = (page: number, page_size: number) => {
        setSearchFilters({...searchFilters, page, page_size});
        clearSearchResultEntries();
    }

    return {
        isSearching,
        searchResults,
        searchErrorMessage,
        searchFilters,
        setSearchFilters,
        clearSearchResultEntries,
        changePage
    }
}